import { useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StatusChip from "../StatusChip";
import { formatDateToShow, formatPhone } from "../../utils/formatters";
import usuarioService from "../../services/usuario";

const SolicitacaoResumoHeader = ({ solicitacao }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [contato, setContato] = useState(null);
  const [carregandoContato, setCarregandoContato] = useState(false);
  const [erroContato, setErroContato] = useState(null);

  if (!solicitacao) return null;

  const solicitadoPorRepresentado = Boolean(solicitacao.solicitadoPorRepresentado);
  const contatoUsuarioId = solicitacao.consultorId || solicitacao.solicitanteId;
  const podeVerContato = Boolean(contatoUsuarioId);

  const handleAbrirContato = async (event) => {
    if (!podeVerContato) return;

    setAnchorEl(event.currentTarget);
    setErroContato(null);

    if (contato) return;

    setCarregandoContato(true);
    try {
      const dados = await usuarioService.obterContato(contatoUsuarioId);
      setContato(dados);
    } catch (error) {
      setErroContato(error.message || "Erro ao obter contato do solicitante");
    } finally {
      setCarregandoContato(false);
    }
  };

  const handleFecharContato = () => {
    setAnchorEl(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
        Informações do Processo
      </Typography>
      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Tipo de Licença
          </Typography>
          <Typography variant="body1">
            {solicitacao.nomeTipoLicenca || "Não especificado"}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Solicitante
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <Typography variant="body1">
              {solicitacao.solicitanteNome || "Não especificado"}
            </Typography>
            {podeVerContato && (
              <>
                <Tooltip title="Contato do solicitante">
                  <IconButton
                    size="small"
                    aria-label="Contato do solicitante"
                    onClick={handleAbrirContato}
                    sx={{ mt: -0.5 }}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleFecharContato}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  <Box sx={{ p: 2, minWidth: 220 }}>
                    {carregandoContato && (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        <CircularProgress size={22} />
                      </Box>
                    )}
                    {!carregandoContato && erroContato && (
                      <Typography variant="body2" color="error">
                        {erroContato}
                      </Typography>
                    )}
                    {!carregandoContato && !erroContato && contato && (
                      <>
                        <Typography variant="subtitle2" color="text.secondary">
                          Telefone
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {contato.telefone
                            ? formatPhone(contato.telefone)
                            : "—"}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          E-mail
                        </Typography>
                        <Typography variant="body2">
                          {contato.email || "—"}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Popover>
              </>
            )}
          </Box>
          {solicitadoPorRepresentado && solicitacao.consultorNome && (
            <Typography variant="body2" color="text.secondary">
              Consultor: {solicitacao.consultorNome}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Data da Solicitação
          </Typography>
          <Typography variant="body1">
            {formatDateToShow(solicitacao.createAt)}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Status Atual
          </Typography>
          <StatusChip status={solicitacao.status} />
        </Grid>
        {solicitacao.numeroProtocolo && (
          <Grid item xs={12} md={6} lg={3}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Número do Protocolo
            </Typography>
            <Typography variant="body1">{solicitacao.numeroProtocolo}</Typography>
          </Grid>
        )}
        {solicitacao.numeroDaLicenca && (
          <Grid item xs={12} md={6} lg={3}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Número da Licença
            </Typography>
            <Typography variant="body1">{solicitacao.numeroDaLicenca}</Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default SolicitacaoResumoHeader;
